import { useContext, useEffect } from 'react';
import { differenceInDays } from 'date-fns';

import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	ONBOARDING_EXPERIENCES,
	OPTIONAL_NTH_USER_GUIDED_TOUR_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

/**
 * Eligibility hook for optional nth user guided tour experiment with following requirements to be eligible
 * Desktop only
 * In onboarding period (ONBOARDING_PERIOD_DAYS days from tourSeenAt)
 * Licensed user
 * Not founding admin
 * Is new user
 */
export const useIsEligibleForOptionalNthUserGuidedTour = () => {
	const isNav4Enabled = useIsNav4Enabled();
	const isLicensedUser = useIsLicensed();
	const isDesktopUser = !(isMobileUserAgent() || shouldShowMobileWeb());
	const ONBOARDING_PERIOD_DAYS = 7;

	const { isNewUser: isNewUserFromContext } = useContext(SPAViewContext);
	const { setOnboardingState } = useSetOnboardingState();

	const shouldFetchOnboardingState =
		fg('cc_engagement_optional_guided_tour_for_nth_users') && isLicensedUser;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...Object.values(OPTIONAL_NTH_USER_GUIDED_TOUR_ONBOARDING_STATE_KEYS),
		],
		!shouldFetchOnboardingState,
	);

	const {
		isFoundingAdmin,
		tourSeenAt,
		hasDismissedTour,
		hasSeenFinishNudge,
		hasCompletedTour,
		isEnrolledAsNewUser,
	} = deserializeState(onboardingStateData);

	useEffect(() => {
		if (
			isNewUserFromContext &&
			isLicensedUser &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!isEnrolledAsNewUser &&
			onboardingStateData
		) {
			void setOnboardingState({
				key: OPTIONAL_NTH_USER_GUIDED_TOUR_ONBOARDING_STATE_KEYS.isEnrolledAsNewUser,
				value: 'true',
			});
		}
	}, [
		isNewUserFromContext,
		isLicensedUser,
		onboardingStateLoading,
		onboardingStateError,
		isEnrolledAsNewUser,
		onboardingStateData,
		setOnboardingState,
	]);
	const isNewUser = isNewUserFromContext || Boolean(isEnrolledAsNewUser);

	const isNthUser =
		isLicensedUser && !onboardingStateLoading && !onboardingStateError && !isFoundingAdmin;
	const isInOnboardingPeriod =
		tourSeenAt === undefined ||
		(Boolean(tourSeenAt) &&
			differenceInDays(new Date(), new Date(tourSeenAt)) <= ONBOARDING_PERIOD_DAYS);

	const isEligibleUserForTour =
		isDesktopUser &&
		isNav4Enabled &&
		isNthUser &&
		isInOnboardingPeriod &&
		isNewUser &&
		!hasCompletedTour &&
		!hasDismissedTour;
	if (onboardingStateLoading) {
		return {
			isEligibleForTour: false,
			loading: true,
			error: false,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			tourSeenAt,
		};
	}

	if (onboardingStateError) {
		if (isUnauthorizedError(onboardingStateError)) {
			markErrorAsHandled(onboardingStateError);
		}
		return {
			isEligibleForTour: false,
			loading: false,
			error: onboardingStateError,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			tourSeenAt,
		};
	}
	return {
		isEligibleForTour: isEligibleUserForTour,
		loading: false,
		error: false,
		hasDismissedTour,
		hasSeenFinishNudge,
		hasCompletedTour,
		tourSeenAt,
	};
};
